import { Check, Flag, Loader2 } from 'lucide-react'
import { formatDate } from '@/lib/date'
import { cn } from '@/lib/cn'

export interface StageStripRow {
  key: string | number
  department: string
  /** Target or completion date, already formatted or an ISO date string. */
  date?: string | null
  state: 'done' | 'current' | 'pending'
  /** The section a queued job starts with, labelled Next instead of Planned. */
  next?: boolean
  /** Team wording: a section still to come reads Due, not Planned. Customers keep Planned. */
  due?: boolean
  /** Not done and its day has passed: "Late, due" in red. Team pages only. */
  late?: boolean
}

/**
 * Day and month only, for the timeline's narrow cells. Slicing the raw string
 * used to leave these month first while the rest of the system reads DD/MM, so
 * the same date appeared two ways on one screen.
 */
function formatDayMonth(value: string): string {
  return formatDate(value).slice(0, 5)
}

const isLate = (row: StageStripRow) => !!row.late && row.state !== 'done'

/** "Done 28/10", "Late, due 27/10", "Due 29/10" or "Planned 30/10", split so the stepper can stack them. */
function dateParts(row: StageStripRow): { label: string; date: string } | null {
  if (!row.date) return null
  const label =
    row.state === 'done'
      ? 'Done'
      : isLate(row)
        ? 'Late, due'
        : row.state === 'current'
          ? 'Due'
          : row.next
            ? 'Next'
            : row.due
              ? 'Due'
              : 'Planned'
  return { label, date: formatDayMonth(row.date) }
}

const textTone = (row: StageStripRow) =>
  isLate(row)
    ? 'font-medium text-danger'
    : row.state === 'done'
    ? 'text-success'
    : row.state === 'current'
      ? 'text-pending'
      : row.next
        ? 'font-medium text-foreground'
        : 'text-foreground/60'

/**
 * The per-department progress timeline, shared by the customer order page,
 * the team's order page and the public track & trace page. Rows carry their
 * own done/current state so the caller decides how progress is derived - the
 * portal from live job data, the public page from the sanitized stages payload.
 *
 * Phones get a vertical list and wider screens a horizontal stepper, with the
 * same numbered dots, joining line and date wording, so the two read as one
 * design. Seven tiles wrapped three to a row read in a zigzag, and customers
 * could not tell the order of the sections or how far along the job was.
 */
export default function StageStrip({ rows, etd }: { rows: StageStripRow[]; etd?: string | null }) {
  if (rows.length === 0) return null

  return (
    <>
      <StageList rows={rows} etd={etd} />
      <StageStepper rows={rows} etd={etd} />
    </>
  )
}

/** Tick when done, spinner while in progress, its number before that. */
function StageDot({ state, next, index }: { state: StageStripRow['state']; next?: boolean; index: number }) {
  return (
    <span
      className={cn(
        'relative flex size-6 shrink-0 items-center justify-center rounded-full text-2xs font-semibold',
        state === 'done' && 'bg-success text-white',
        state === 'current' && 'border-2 border-pending bg-background text-pending',
        state === 'pending' && !next && 'border-2 border-foreground/20 bg-background text-foreground/50',
        state === 'pending' && next && 'border-2 border-foreground/60 bg-background text-foreground',
      )}
    >
      {state === 'done' ? <Check className="size-3.5" /> : state === 'current' ? <Loader2 className="size-3" /> : index + 1}
    </span>
  )
}

function EtdDot() {
  return (
    <span className="relative flex size-6 shrink-0 items-center justify-center rounded-full border-2 border-foreground/30 bg-background text-foreground/70">
      <Flag className="size-3" />
    </span>
  )
}

const nameClass = (row: StageStripRow) =>
  cn(
    row.state === 'done' && 'font-medium text-foreground',
    row.state === 'current' && 'font-semibold text-pending',
    row.state === 'pending' && (row.next ? 'font-semibold text-foreground' : 'text-foreground/60'),
  )

/** Phones: one section per line, top to bottom, joined by a line like a parcel tracker. */
function StageList({ rows, etd }: { rows: StageStripRow[]; etd?: string | null }) {
  return (
    <ol className="sm:hidden">
      {rows.map((row, i) => {
        const last = i === rows.length - 1 && !etd
        const parts = dateParts(row)
        return (
          <li key={row.key} className="relative flex gap-3 pb-4 last:pb-0">
            {!last && (
              <span
                aria-hidden
                className={cn(
                  'absolute bottom-0 left-3 top-6 w-px -translate-x-1/2',
                  row.state === 'done' ? 'bg-success/60' : 'bg-foreground/15',
                )}
              />
            )}
            <StageDot state={row.state} next={row.next} index={i} />
            <div className="flex min-w-0 flex-1 items-baseline justify-between gap-3 pt-0.5">
              <span className={cn('text-sm', nameClass(row))}>{row.department}</span>
              <span className={cn('shrink-0 text-xs', textTone(row))}>
                {parts ? `${parts.label} ${parts.date}` : '-'}
              </span>
            </div>
          </li>
        )
      })}
      {etd && (
        <li className="relative flex gap-3">
          <EtdDot />
          <div className="flex min-w-0 flex-1 items-baseline justify-between gap-3 pt-0.5">
            <span className="text-sm font-semibold">Ready (ETD)</span>
            <span className="shrink-0 text-xs font-semibold">{formatDayMonth(etd)}</span>
          </div>
        </li>
      )}
    </ol>
  )
}

/** From sm up: the same steps left to right, the name and date under each dot. */
function StageStepper({ rows, etd }: { rows: StageStripRow[]; etd?: string | null }) {
  return (
    <ol className="hidden sm:flex">
      {rows.map((row, i) => {
        const last = i === rows.length - 1 && !etd
        const parts = dateParts(row)
        return (
          <li key={row.key} className="relative flex min-w-0 flex-1 flex-col items-center px-1 text-center">
            {/* From this dot's centre to the next one's: green once this section is done. */}
            {!last && (
              <span
                aria-hidden
                className={cn(
                  'absolute left-1/2 top-3 h-px w-full',
                  row.state === 'done' ? 'bg-success/60' : 'bg-foreground/15',
                )}
              />
            )}
            <StageDot state={row.state} next={row.next} index={i} />
            <span className={cn('mt-2 text-xs leading-tight', nameClass(row))}>{row.department}</span>
            {parts ? (
              <span className={cn('mt-1 text-2xs leading-tight', textTone(row))}>
                {parts.label}
                <br />
                <span className="font-semibold">{parts.date}</span>
              </span>
            ) : (
              <span className="mt-1 text-2xs text-foreground/60">-</span>
            )}
          </li>
        )
      })}
      {etd && (
        <li className="relative flex min-w-0 flex-1 flex-col items-center px-1 text-center">
          <EtdDot />
          <span className="mt-2 text-xs font-semibold leading-tight">Ready (ETD)</span>
          <span className="mt-1 text-2xs leading-tight text-foreground/60">
            Target
            <br />
            <span className="font-semibold text-foreground">{formatDayMonth(etd)}</span>
          </span>
        </li>
      )}
    </ol>
  )
}
